import gql from 'graphql-tag';
import { graphql } from 'react-apollo';

import identityDetails from '../../../../../graphql/fragments/identity/identityDetails';
import photoAttributionDetails from '../fragments/photoAttributionDetails';
import {
  PhotoFavoriteDetails,
  PhotoForegroundDetails,
  PhotoWallpaperWatermarkDetails,
} from '../../../../../../../graphql/fragments/photo';
import {
  AlbumAlbumFolderTagDetails,
  AlbumFolderTagCustomPrintDetails,
} from '../../../../../../../graphql/fragments/album-folder-tags';
import { MatchedPhotoYearbookPhotoDetails } from '../../../../../../../graphql/fragments/yearbook-photos';

export const getAlbumMembershipGroupIdentityQuery = gql`
  query getAlbumMembershipGroupIdentities(
    $id: RecordID!
    $identityId: RecordID!
    $limit: Int = 50
    $offset: Int = 0
    $album_folder_tag_ids: [RecordID]
    $matched_photos_sort: MatchedPhotosSortEnum
  ) {
    albumMembershipGroup(
      id: $id
      root_groups_only: true
      include_matched_photos_only: true
    ) {
      id
      identities(identity_ids: [$identityId]) {
        identity {
          ...identityDetails
        }
        matched_photos_extended(
          slice: { limit: $limit, offset: $offset }
          album_folder_tag_ids: $album_folder_tag_ids
          sort_photos_with_album_folder_tags_last: true
          matched_photos_sort: $matched_photos_sort
        ) {
          total
          count
          records {
            id
            identity {
              id
              name
            }
            photo {
              id
              thumbnail_urls(ignore_watermark: true) {
                url
                size_code
                width
                height
              }
              attribution {
                ...photoAttributionDetails
              }
              share_blocked
              is_entitled_matched_photo
              is_owner
              ...PhotoFavoriteDetails
              ...PhotoForegroundDetails
              faces(
                slice: { limit: 1 }
                exclude_machine_labeled_pub_boxes: true
                exclude_human_labeled_pub_boxes: true
              ) {
                total
                records {
                  facebox
                }
              }
              ...PhotoWallpaperWatermarkDetails
            }
            compared_face {
              id
              facebox
            }
            album {
              id
              name
              album_type
              album_subtype
              picture_day_enabled
              enforce_pricing_tag_digital_downloads
              custom_prints_meta(custom_print_types: [WALDOGRAM]) {
                onsite_delivery_enabled
                offsite_shipping_enabled
              }
              price_whole_album {
                value
                currency
              }
              root_album_folder {
                id
                enforce_pricing_tag_digital_downloads
                pricing_categories {
                  total
                  records {
                    id
                    pricing_category {
                      id
                      name
                    }
                  }
                }
              }
              pricing_categories {
                total
                records {
                  id
                  pricing_category {
                    id
                    name
                  }
                }
              }
              album_folder_tags(
                album_folder_tag_types: UNIFORM
                slice: { limit: 999 }
              ) {
                records {
                  ...AlbumAlbumFolderTagDetails
                }
              }
            }
            has_purchased_album_identity
            price_album_folder_tag {
              ... on IRecord {
                id
              }
              ... on IAlbumFolderTag {
                name
                type
                is_pricing_tag
                digital_downloads_enabled
                digital_downloads_price {
                  value
                  currency
                }
                print_product_items {
                  total
                  records {
                    id
                    print_product {
                      id
                      name
                    }
                    print_product_size {
                      id
                      print_size {
                        id
                        name
                      }
                      price {
                        value
                        currency
                      }
                    }
                    price {
                      value
                      currency
                    }
                  }
                }
              }
              ...AlbumFolderTagCustomPrintDetails
            }
            faceblocked_thumbnail_urls {
              url
              size_code
              width
              height
              blur_type
              regions
            }
            ...MatchedPhotoYearbookPhotoDetails
          }
        }
        entitled_matched_photos(slice: { limit: 999 }) {
          total
          records {
            id
          }
        }
        purchased_print_packages {
          total
        }
      }
    }
  }
  ${identityDetails}
  ${photoAttributionDetails}
  ${PhotoFavoriteDetails}
  ${PhotoForegroundDetails}
  ${PhotoWallpaperWatermarkDetails}
  ${AlbumAlbumFolderTagDetails}
  ${AlbumFolderTagCustomPrintDetails}
  ${MatchedPhotoYearbookPhotoDetails}
`;

const getAlbumMembershipGroupIdentityOptions = {
  options: ({
    albumMembershipGroupId,
    identityId,
    albumMembershipGroupFilters,
    matchedPhotosSort,
  }) => ({
    variables: {
      id: albumMembershipGroupId,
      identityId,
      album_folder_tag_ids: albumMembershipGroupFilters,
      matched_photos_sort: matchedPhotosSort,
    },
    fetchPolicy: 'cache-first',
  }),
  skip: ({ albumMembershipGroupId, identityId }) =>
    !albumMembershipGroupId || !identityId,
};

const getAlbumMembershipGroupIdentity = graphql(
  getAlbumMembershipGroupIdentityQuery,
  getAlbumMembershipGroupIdentityOptions,
);

export default getAlbumMembershipGroupIdentity;
